<script lang="ts">
  import { APP_VERSION } from "../../lib/buildinfo";
  import { isNativeIos } from "../../lib/config";
  import { desktopCatalog, releaseUrl, type DesktopOs } from "../../lib/downloads";

  // OS glyphs are inline SVG (currentColor) so they crisp-render at any DPR.
  type Platform = { os: DesktopOs; name: string; icon: "windows" | "apple" | "linux" };
  const platforms: Platform[] = [
    { os: "windows", name: "Windows", icon: "windows" },
    { os: "macos", name: "macOS", icon: "apple" },
    { os: "linux", name: "Linux", icon: "linux" },
  ];

  // Download URL = /releases/<version>/<file>; version is baked at build time.
  const downloadUrl = (os: DesktopOs) => {
    const v = desktopCatalog(APP_VERSION).find((g) => g.os === os)?.variants[0];
    return v ? releaseUrl(APP_VERSION, v.file) : "#";
  };

  // CLI one-line installer.
  const cliCommands = {
    unix: "curl -fsSL https://optioway.com/install | bash",
    windows: 'powershell -c "irm optioway.com/install.ps1 | iex"',
  } as const;
  let cliTab = $state<"unix" | "windows">("unix");
  let copied = $state(false);
  async function copyCli() {
    try {
      await navigator.clipboard.writeText(cliCommands[cliTab]);
      copied = true;
      setTimeout(() => (copied = false), 1500);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  }
</script>

<section class="max-w-3xl mx-auto px-6 py-16">
  {#if isNativeIos()}
    <h1 class="text-3xl font-bold">You're on the Optio app</h1>
    <p class="mt-4 text-slate-400">
      You're already running Optio on this device. Desktop apps for Windows, macOS, and Linux are
      available on the Optio website.
    </p>
  {:else}
    <h1 class="text-3xl font-bold">Download Optio</h1>
    <p class="mt-4 text-slate-400">
      Install the app, sign in, and your plan is applied automatically.
    </p>
    <div class="mt-10 grid gap-4 sm:grid-cols-3">
      {#each platforms as p (p.name)}
        <div
          class="group flex flex-col items-center rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center transition-colors hover:border-indigo-400/40 hover:bg-white/[0.04]"
        >
          <div class="text-slate-300 transition-colors group-hover:text-indigo-300">
            {#if p.icon === "windows"}
              <!-- Windows logo: four tiles, slightly skewed like the brand mark. -->
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M3 5.5 11 4.3v7.2H3V5.5Zm0 7h8v7.2L3 18.5v-6Zm9-8.3L21 3v8.5h-9V4.2Zm0 8.3h9V21l-9-1.2v-7.3Z"
                />
              </svg>
            {:else if p.icon === "apple"}
              <!-- Apple silhouette: bitten apple with leaf. -->
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M16.4 12.5c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.1 1.8 2.4 3 2.4 1.2-.1 1.7-.8 3.2-.8s1.9.8 3.2.7c1.3 0 2.2-1.2 3-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.5-1-2.5-3.9Zm-2.3-7.2c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.8 1.4-.6.7-1.2 1.8-1 2.9 1 .1 2.1-.5 2.8-1.3Z"
                />
              </svg>
            {:else}
              <!-- Linux / Tux: simplified penguin silhouette. -->
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M12 1.6c-2.5 0-4 2-4 4.7 0 1.4.3 2.6.7 3.7-.4.5-1 1.2-1.7 2.3-1 1.5-1.8 3-1.8 4.2 0 .8.4 1.4 1 1.7-.1.2-.1.5-.1.7 0 .9.6 1.6 1.6 1.9.7.2 1.6.3 2.3 0 .6-.3 1.1-.8 2-.8s1.4.5 2 .8c.7.3 1.6.2 2.3 0 1-.3 1.6-1 1.6-1.9 0-.2 0-.5-.1-.7.6-.3 1-.9 1-1.7 0-1.2-.8-2.7-1.8-4.2-.7-1.1-1.3-1.8-1.7-2.3.4-1.1.7-2.3.7-3.7 0-2.7-1.5-4.7-4-4.7Zm-1.4 4.3c.5 0 .9.5.9 1.2s-.4 1.2-.9 1.2c-.2 0-.4-.1-.6-.3.1-.1.3-.2.3-.4 0-.3-.3-.5-.6-.5-.1 0-.3 0-.4.1.1-.7.6-1.3 1.3-1.3Zm2.8 0c.7 0 1.2.6 1.3 1.3-.1-.1-.3-.1-.4-.1-.3 0-.6.2-.6.5 0 .2.1.3.3.4-.2.2-.4.3-.6.3-.5 0-.9-.5-.9-1.2s.4-1.2.9-1.2Zm-1.4 4c.7 0 1.6.5 1.6 1.1 0 .2-.4.4-.9.6-.3.1-.5.3-.7.3-.2 0-.4-.2-.7-.3-.5-.2-.9-.4-.9-.6 0-.6.9-1.1 1.6-1.1Z"
                />
              </svg>
            {/if}
          </div>
          <div class="mt-4 font-medium">{p.name}</div>
          <a
            href={downloadUrl(p.os)}
            download
            class="mt-5 rounded-md border border-indigo-400/40 bg-indigo-500/15 px-4 py-2 text-sm font-medium text-indigo-100 transition-colors hover:border-indigo-300/60 hover:bg-indigo-500/25"
          >
            Download
          </a>
        </div>
      {/each}
    </div>

    <!-- Command-line tool: one-line installer for the headless `optio` agent. -->
    <div class="mt-16 border-t border-white/10 pt-12">
      <h2 class="text-2xl font-bold">Command-line tool</h2>
      <p class="mt-3 text-slate-400">
        One command installs the <code class="text-slate-300">optio</code> binary on your PATH.
      </p>

      <div class="mt-6 flex gap-2">
        <button
          class="rounded-md px-3 py-1.5 text-sm transition-colors {cliTab === 'unix'
            ? 'bg-indigo-500/15 text-indigo-200'
            : 'text-slate-400 hover:text-slate-200'}"
          onclick={() => (cliTab = "unix")}
        >
          macOS / Linux
        </button>
        <button
          class="rounded-md px-3 py-1.5 text-sm transition-colors {cliTab === 'windows'
            ? 'bg-indigo-500/15 text-indigo-200'
            : 'text-slate-400 hover:text-slate-200'}"
          onclick={() => (cliTab = "windows")}
        >
          Windows
        </button>
      </div>

      <div
        class="mt-3 flex items-center gap-3 rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm"
      >
        <span class="select-none text-indigo-400">$</span>
        <code class="flex-1 overflow-x-auto whitespace-nowrap text-slate-200"
          >{cliCommands[cliTab]}</code
        >
        <button
          class="shrink-0 rounded-md border border-white/15 px-2.5 py-1 text-xs text-slate-300 transition-colors hover:border-indigo-400/40 hover:text-indigo-200"
          onclick={copyCli}
          aria-label="Copy install command"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>

    <!-- Mobile — official App Store / Google Play badges. The iOS + Android
         apps aren't published yet, so the badges are dimmed + non-clickable;
         wrap each in <a href="<store-url>"> once live. Artwork is the official
         Apple / Google badge files in public/ (do not redraw — store brand
         guidelines require the unmodified badge). -->
    <div class="mt-16 flex flex-col items-center border-t border-white/10 pt-12 text-center">
      <h2 class="text-2xl font-bold">Optio on mobile</h2>
      <p class="mt-3 text-slate-400">iPhone and Android apps — coming soon.</p>
      <div class="mt-6 flex flex-wrap items-center justify-center gap-4">
        <span
          aria-disabled="true"
          title="Coming soon to the App Store"
          class="cursor-not-allowed opacity-50"
        >
          <img src="/app-store-badge.svg" alt="Download on the App Store" class="h-10 w-auto" />
        </span>
        <span
          aria-disabled="true"
          title="Coming soon to Google Play"
          class="cursor-not-allowed opacity-50"
        >
          <img src="/google-play-badge.png" alt="Get it on Google Play" class="h-[52px] w-auto" />
        </span>
      </div>
    </div>
  {/if}
</section>
